import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  GitBranch,
  Loader2,
  MessageSquare,
  Phone,
  PlayCircle,
  Upload,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useProject } from '../../contexts/ProjectContext';
import api from '../../api';
import { trackedChangeAttributionLabel } from './trackedChangeAttribution';

type SourceKind = 'redline' | 'commented_sop' | 'call_notes' | 'other';

interface DetectedChange {
  id: number;
  change_type: 'modify' | 'add' | 'delete';
  section_number: string | null;
  section_title: string | null;
  original_snippet: string | null;
  proposed_content: string;
  proposed_title: string | null;
  replace_entire_section: boolean;
  field: 'content' | 'title';
  reasoning: string;
  confidence: number;
  source_signal: string;
  match_method?: string;
  current_content?: string;
  status?: string | null;
  skip_reason?: string | null;
  author?: string | null;
  authors?: string[];
  date?: string | null;
}

interface ImpactSummary {
  total_changes: number;
  modify_count: number;
  add_count: number;
  delete_count: number;
  affected_sections: number;
  high_confidence_changes: number;
  overall_confidence: number;
}

interface ExtractionSummary {
  tracked_changes: number;
  comments: number;
  deterministic_matches: number;
  unmatched_tracked_changes: number;
  ai_changes: number;
  source_chars: number;
  filename: string | null;
  errors: string[];
}

const SOURCE_KINDS: Array<{
  id: SourceKind;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    id: 'redline',
    label: 'Redlined SOP',
    description: 'Tracked-changes Word file the client sent back',
    icon: GitBranch,
  },
  {
    id: 'commented_sop',
    label: 'Commented SOP',
    description: 'Our SOP returned with review comments in the margin',
    icon: MessageSquare,
  },
  {
    id: 'call_notes',
    label: 'Call notes',
    description: 'Notes from walking the SOP with the client',
    icon: Phone,
  },
  {
    id: 'other',
    label: 'Other document',
    description: 'A policy, excerpt, or note that updates part of the SOP',
    icon: FileText,
  },
];

const ACCEPT = '.docx,.pdf,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';

function isApplyEligible(change: DetectedChange) {
  return change.match_method !== 'unmapped'
    && change.match_method !== 'skipped'
    && change.match_method !== 'conflict'
    && change.status !== 'unmapped'
    && change.status !== 'skipped'
    && change.status !== 'conflict'
    && change.skip_reason !== 'whitespace_only';
}

interface SOPUpdateChangeProps {
  sop?: { id?: number; title?: string; content?: any } | null;
  onApplied?: () => void;
}

const SOPUpdateChange: React.FC<SOPUpdateChangeProps> = ({ sop, onApplied }) => {
  const { currentProject } = useProject();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [sourceKind, setSourceKind] = useState<SourceKind>('redline');
  const [file, setFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState('');
  const [sourceReference, setSourceReference] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [updateRequestId, setUpdateRequestId] = useState<number | null>(null);
  const [detectedChanges, setDetectedChanges] = useState<DetectedChange[]>([]);
  const [impactSummary, setImpactSummary] = useState<ImpactSummary | null>(null);
  const [extractionSummary, setExtractionSummary] = useState<ExtractionSummary | null>(null);
  const [approvedIds, setApprovedIds] = useState<Set<number>>(new Set());

  const hasSop = Boolean(sop?.id);
  const canParse = hasSop && Boolean(file || pastedText.trim());

  const placeholder = useMemo(() => {
    switch (sourceKind) {
      case 'redline':
        return 'Optional: paste additional context. The tracked changes in the Word file are read automatically.';
      case 'commented_sop':
        return 'Optional: paste extra notes. Comments in the Word file are read automatically.';
      case 'call_notes':
        return 'Paste call notes, or upload a transcript / notes file…';
      default:
        return 'Paste the update, or upload the document that should flow into the SOP…';
    }
  }, [sourceKind]);

  const onFile = (next: File | null) => {
    if (!next) {
      setFile(null);
      return;
    }
    const name = next.name.toLowerCase();
    const ok = name.endsWith('.docx') || name.endsWith('.pdf') || name.endsWith('.txt') || name.endsWith('.md');
    if (!ok) {
      toast.error('Upload a Word (.docx), PDF, or text file');
      return;
    }
    setFile(next);
  };

  const resetResults = () => {
    setDetectedChanges([]);
    setImpactSummary(null);
    setExtractionSummary(null);
    setUpdateRequestId(null);
    setApprovedIds(new Set());
  };

  const handleParse = async () => {
    if (!currentProject?.id || !canParse) {
      toast.error('Upload a document or paste notes first');
      return;
    }

    setParsing(true);
    try {
      const form = new FormData();
      form.append('project_id', String(currentProject.id));
      if (sop?.id) form.append('sop_id', String(sop.id));
      form.append('source_kind', sourceKind);
      if (sourceReference.trim()) form.append('source_reference', sourceReference.trim());
      if (pastedText.trim()) form.append('pasted_text', pastedText.trim());
      if (file) form.append('file', file);

      const response = await api.post('/api/sop-updates/parse', form, {
        timeout: 300000,
      });

      const changes: DetectedChange[] = response.data.detected_changes || [];
      setDetectedChanges(changes);
      setImpactSummary(response.data.impact_summary || null);
      setExtractionSummary(response.data.extraction_summary || null);
      setUpdateRequestId(response.data.update_request_id);
      setApprovedIds(new Set(changes.filter(isApplyEligible).map((c) => c.id)));

      if (changes.length === 0) {
        toast('No SOP updates were found in that source', { icon: 'ℹ️' });
      } else {
        toast.success(`Found ${changes.length} update${changes.length === 1 ? '' : 's'} to review`);
      }
    } catch (error: any) {
      console.error('SOP update parse error:', error);
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to parse the document');
    } finally {
      setParsing(false);
    }
  };

  const toggleApproval = (id: number) => {
    const change = detectedChanges.find((c) => c.id === id);
    if (change && !isApplyEligible(change)) return;
    setApprovedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = async () => {
    if (!updateRequestId) {
      toast.error('Parse a document first');
      return;
    }
    if (approvedIds.size === 0) {
      toast.error('Approve at least one change');
      return;
    }

    setApplying(true);
    try {
      const response = await api.post(`/api/sop-updates/${updateRequestId}/apply`, {
        selected_change_ids: Array.from(approvedIds),
      });
      const applied = response.data.applied_count || 0;
      const failed = response.data.failed_count || 0;
      const skipped = response.data.skipped_count || 0;
      if (applied === 0) {
        const bits = ['No updates were written to the SOP'];
        if (skipped > 0) bits.push(`${skipped} skipped`);
        if (failed > 0) bits.push(`${failed} could not be applied`);
        toast.error(bits.join('; '));
        return;
      }
      if (failed > 0 || skipped > 0) {
        const bits = [`Applied ${applied} update${applied === 1 ? '' : 's'}`];
        if (skipped > 0) bits.push(`${skipped} skipped`);
        if (failed > 0) bits.push(`${failed} could not be applied`);
        toast(bits.join('; '), { icon: '⚠️' });
      } else {
        toast.success(`Applied ${applied} update${applied === 1 ? '' : 's'} to the SOP`);
      }
      resetResults();
      setPastedText('');
      setFile(null);
      if (onApplied) onApplied();
    } catch (error: any) {
      console.error('SOP update apply error:', error);
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to apply updates');
    } finally {
      setApplying(false);
    }
  };

  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const dropped = event.dataTransfer.files?.[0];
    if (dropped) onFile(dropped);
  }, []);

  const typeColor = (type: string) => {
    if (type === 'add') return 'bg-green-100 text-green-800 border-green-300';
    if (type === 'delete') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  if (!hasSop) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <h2 className="text-lg font-semibold text-amber-900">Generate the SOP first</h2>
          <p className="text-sm text-amber-800 mt-1">
            Update/Change reads client markup against the current SOP and writes the edits into it. Complete Step 5, then come back here with the redline, comments, or notes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-super-dark-purple">Update / Change</h1>
        <p className="text-gray-600 mt-1">
          Load the client&apos;s redline, commented SOP, call notes, or another update document. Jarvis reads the markup, maps it onto the current SOP, and applies only the content that changed — formatting, numbering, and provenance stay in place.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">What are you loading?</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {SOURCE_KINDS.map((kind) => {
              const Icon = kind.icon;
              const selected = sourceKind === kind.id;
              return (
                <button
                  key={kind.id}
                  type="button"
                  onClick={() => setSourceKind(kind.id)}
                  className={`text-left p-4 rounded-lg border-2 transition-colors ${
                    selected
                      ? 'border-electric-blue bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <Icon className={`w-5 h-5 mb-2 ${selected ? 'text-electric-blue' : 'text-gray-500'}`} />
                  <div className="font-medium text-gray-900">{kind.label}</div>
                  <div className="text-xs text-gray-600 mt-1">{kind.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Document</label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-electric-blue bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-800">
                <FileText className="w-4 h-4" />
                <span>{file.name}</span>
                <button
                  type="button"
                  className="p-1 text-gray-500 hover:text-red-600"
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-700">Drop a Word, PDF, or text file here, or click to browse</p>
                <p className="text-xs text-gray-500 mt-1">.docx keeps tracked changes and comments. PDF and text work for notes.</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0] || null)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Reference (optional)</label>
          <input
            type="text"
            value={sourceReference}
            onChange={(e) => setSourceReference(e.target.value)}
            placeholder="e.g., Client redline — 26 Aug 2026"
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {file ? 'Additional notes (optional)' : 'Notes or pasted content'}
          </label>
          <textarea
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            rows={8}
            placeholder={placeholder}
            className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleParse}
            disabled={parsing || !canParse}
            className="bg-electric-blue text-white px-6 py-2 rounded-md hover:bg-darker-blue disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {parsing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Reading changes…</span>
              </>
            ) : (
              <>
                <PlayCircle className="w-4 h-4" />
                <span>Parse into SOP</span>
              </>
            )}
          </button>
        </div>
      </div>

      {extractionSummary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
          Read {extractionSummary.tracked_changes} tracked change{extractionSummary.tracked_changes === 1 ? '' : 's'}
          {' '}and {extractionSummary.comments} comment{extractionSummary.comments === 1 ? '' : 's'}
          {extractionSummary.deterministic_matches > 0 && (
            <> · {extractionSummary.deterministic_matches} matched the SOP directly</>
          )}
          {extractionSummary.ai_changes > 0 && (
            <> · {extractionSummary.ai_changes} interpreted from comments or notes</>
          )}
        </div>
      )}

      {impactSummary && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Impact</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-2xl font-bold text-blue-600">{impactSummary.total_changes}</div>
              <div className="text-sm text-gray-600">Proposed updates</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{impactSummary.add_count}</div>
              <div className="text-sm text-gray-600">Additions</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{impactSummary.modify_count}</div>
              <div className="text-sm text-gray-600">Modifications</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{impactSummary.delete_count}</div>
              <div className="text-sm text-gray-600">Deletions</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">{impactSummary.affected_sections}</div>
              <div className="text-sm text-gray-600">SOP sections</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{impactSummary.high_confidence_changes}</div>
              <div className="text-sm text-gray-600">High confidence</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-700">{Math.round((impactSummary.overall_confidence || 0) * 100)}%</div>
              <div className="text-sm text-gray-600">Avg confidence</div>
            </div>
          </div>
        </div>
      )}

      {detectedChanges.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Proposed SOP updates</h3>
            <div className="text-sm text-gray-600">
              {approvedIds.size} of {detectedChanges.length} approved
            </div>
          </div>

          <div className="space-y-4">
            {detectedChanges.map((change, index) => {
              const attribution = trackedChangeAttributionLabel(change);
              return (
              <div
                key={change.id}
                className={`border-2 rounded-lg p-4 ${
                  approvedIds.has(change.id) ? 'border-green-300 bg-green-50' : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-3 gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${typeColor(change.change_type)}`}>
                        {change.change_type.toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-gray-700">
                        {change.section_number} {change.section_title}
                      </span>
                      {change.match_method === 'deterministic' && (
                        <span className="text-xs text-green-700">Matched from tracked changes</span>
                      )}
                      {(change.status === 'skipped' || change.match_method === 'skipped') && (
                        <span className="text-xs text-gray-600">Skipped</span>
                      )}
                      {(change.status === 'unmapped' || change.match_method === 'unmapped') && (
                        <span className="text-xs text-amber-700">Unmapped</span>
                      )}
                      {(change.status === 'conflict' || change.match_method === 'conflict') && (
                        <span className="text-xs text-red-700">Conflict</span>
                      )}
                      {change.confidence >= 0.85 && (
                        <span className="flex items-center gap-1 text-green-600 text-xs">
                          <CheckCircle2 className="w-3 h-3" />
                          High confidence
                        </span>
                      )}
                    </div>
                    {attribution && (
                      <p className="text-xs text-gray-500 mt-1 break-words">
                        {attribution}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleApproval(change.id)}
                    disabled={!isApplyEligible(change)}
                    className={`px-4 py-1 rounded-md text-sm font-medium flex-shrink-0 ${
                      !isApplyEligible(change)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : approvedIds.has(change.id)
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {isApplyEligible(change)
                      ? (approvedIds.has(change.id) ? 'Approved' : 'Approve')
                      : 'Not applicable'}
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  {(change.current_content || change.original_snippet) && (
                    <div className="bg-red-50 border border-red-200 rounded p-2">
                      <span className="font-medium text-red-800">Current</span>
                      <p className="text-gray-700 mt-1 whitespace-pre-wrap">
                        {change.original_snippet || change.current_content}
                      </p>
                    </div>
                  )}
                  <div className="bg-green-50 border border-green-200 rounded p-2">
                    <span className="font-medium text-green-800">
                      {change.proposed_title ? 'Proposed title / content' : 'Proposed'}
                    </span>
                    <p className="text-gray-700 mt-1 whitespace-pre-wrap">
                      {change.proposed_title ? `${change.proposed_title}\n${change.proposed_content || ''}`.trim() : change.proposed_content}
                    </p>
                  </div>
                  {change.reasoning && (
                    <div className="bg-gray-50 border border-gray-200 rounded p-2">
                      <span className="font-medium">Why</span>
                      <p className="text-gray-700 mt-1">{change.reasoning}</p>
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    Change #{index + 1} · {Math.round((change.confidence || 0) * 100)}% confidence
                    {change.source_signal ? ` · ${change.source_signal.replace('_', ' ')}` : ''}
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={resetResults}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={applying || approvedIds.size === 0}
              className="bg-electric-blue text-white px-6 py-2 rounded-md hover:bg-darker-blue disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {applying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Applying…</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Apply {approvedIds.size} approved update{approvedIds.size === 1 ? '' : 's'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPUpdateChange;
